import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Id, toast, ToastContainer } from 'react-toastify';
import { useDismissSuggestionMutation } from 'store/services/tradeSuggestions/tradeSuggestionsApi';

/** The undo window. The release request does not fire until this closes. */
export const UNDO_WINDOW_MS = 8000;

/**
 * Own container so the undo toast can be centred on the CONTENT area while the
 * app's ordinary toasts stay top-right. A single container cannot hold two
 * positions, and toastify animates `transform` on the toast element, so a
 * static translate on the toast itself would fight the entry animation — the
 * offset therefore lives on the container.
 */
export const UNDO_TOAST_CONTAINER_ID = 'suggestion-undo';

interface PendingPass {
  timerId: number;
  toastId: Id;
}

interface SuggestionPassContextValue {
  /**
   * Suggestions the user has acted on this session. They stay in the deck's
   * queue as numbering history, so this is "handled", not "removed".
   */
  passedIds: ReadonlySet<string>;
  /** Bumped by undo so a mounted deck can jump back to the restored card */
  lastRestoredId: string | null;
  pass: (suggestionId: string, counterpartyUsername: string) => void;
  undo: (suggestionId: string) => void;
}

const SuggestionPassContext = createContext<SuggestionPassContextValue | null>(null);

/**
 * Pass with a deferred release, hoisted ABOVE the Offers tab host.
 *
 * The dismiss request is HELD for the whole undo window and only fires when it
 * closes. Undo clears the timer, so the undo path issues ZERO network requests
 * — nothing to roll back, because nothing was ever sent. That is why pass needs
 * no separate undo endpoint.
 *
 * WHY THIS LIVES ABOVE THE TABS (Eric's ruling, 2026-08-04). Suggested is a tab,
 * so switching to Received unmounts the deck. If the timer and the toast lived
 * in the deck, that tab click would run the unmount flush below and commit the
 * pass — one click inside the window would release the bundle and take the undo
 * away with it. Mounted here, a tab switch unmounts the deck but not the pending
 * pass: the timer keeps running, the toast stays up, and undo works from any tab.
 *
 * Leaving /trade-offers entirely still commits, because this provider unmounts
 * with the page. That is deliberate and unchanged: only the INTRA-PAGE tab
 * switch must not commit.
 *
 * `passedIds` is React state rather than a ref precisely because undo can happen
 * while the deck is unmounted — the deck reads the set on its next mount instead
 * of owning it.
 */
export const SuggestionPassProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dismissSuggestion] = useDismissSuggestionMutation();
  const [passedIds, setPassedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [lastRestoredId, setLastRestoredId] = useState<string | null>(null);
  const pending = useRef(new Map<string, PendingPass>());

  // Kept in a ref so the unmount effect below can stay dependency-free and
  // therefore run ONLY on unmount, never on a re-render that changed identity.
  const dismissRef = useRef(dismissSuggestion);
  dismissRef.current = dismissSuggestion;

  const release = useCallback((suggestionId: string) => {
    pending.current.delete(suggestionId);
    dismissRef.current(suggestionId)
      .unwrap()
      // Dismiss is 200-idempotent server-side; a failure here means the request
      // never landed. The suggestion stays ACTIVE and returns on the next fetch,
      // which is the honest outcome — do not fake a local removal.
      .catch(() => undefined);
  }, []);

  const undo = useCallback((suggestionId: string) => {
    const entry = pending.current.get(suggestionId);
    if (!entry) return;
    window.clearTimeout(entry.timerId);
    toast.dismiss(entry.toastId);
    pending.current.delete(suggestionId);
    setPassedIds((current) => {
      const next = new Set(current);
      next.delete(suggestionId);
      return next;
    });
    setLastRestoredId(suggestionId);
  }, []);

  const pass = useCallback(
    (suggestionId: string, counterpartyUsername: string) => {
      const timerId = window.setTimeout(() => release(suggestionId), UNDO_WINDOW_MS);
      // Undo is an explicit control, not a click-anywhere affordance: the whole
      // point of the window is that the user can SEE the escape hatch. Reference
      // Copy verbatim, including the trailing [Undo].
      const toastId = toast.info(
        <span className="sugg-pass-toast">
          Passed on {counterpartyUsername}. These cards are free for other traders.
          <button type="button" className="sugg-pass-undo" onClick={() => undo(suggestionId)}>
            Undo
          </button>
        </span>,
        {
          autoClose: UNDO_WINDOW_MS,
          closeOnClick: false,
          containerId: UNDO_TOAST_CONTAINER_ID,
        }
      );
      pending.current.set(suggestionId, { timerId, toastId });
      setPassedIds((current) => new Set(current).add(suggestionId));
    },
    [release, undo]
  );

  useEffect(() => {
    const map = pending.current;
    return () => {
      map.forEach(({ timerId, toastId }, suggestionId) => {
        window.clearTimeout(timerId);
        toast.dismiss(toastId);
        dismissRef.current(suggestionId)
          .unwrap()
          .catch(() => undefined);
      });
      map.clear();
    };
  }, []);

  const value = useMemo(
    () => ({ passedIds, lastRestoredId, pass, undo }),
    [passedIds, lastRestoredId, pass, undo]
  );

  return (
    <SuggestionPassContext.Provider value={value}>
      {children}
      {/*
        Centred on the content area, not the viewport, so it does not sit under
        the sidebar. Decision 14 specifies calc(50% + 125px) against the 250px
        sidebar; the collapsed 72px state is NOT compensated for, per that
        decision as written. See SuggestionPassContext.css.
      */}
      <ToastContainer
        containerId={UNDO_TOAST_CONTAINER_ID}
        className="sugg-undo-toast-container"
        position="bottom-center"
        hideProgressBar={false}
        newestOnTop
        closeOnClick={false}
        draggable={false}
        pauseOnFocusLoss={false}
        theme="dark"
      />
    </SuggestionPassContext.Provider>
  );
};

/**
 * Throws outside the provider rather than returning a no-op: a silently inert
 * pass would look like the animation simply failing, and the bundle would never
 * be released.
 */
export const useSuggestionPassContext = (): SuggestionPassContextValue => {
  const ctx = useContext(SuggestionPassContext);
  if (!ctx) {
    throw new Error('useSuggestionPassContext must be used inside SuggestionPassProvider');
  }
  return ctx;
};

export default SuggestionPassContext;
